import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { wgs84ToGcj02 } from "./geo";

// Base map: 天地图 official WMTS (WGS-84 native, NO datum offset) via our own
// same-origin /tiles proxy (admin is served from sanxia.pgv.sh.cn, same origin
// as the proxy; the proxy injects the browser UA + whitelisted Referer + tk the
// 浏览器端 tk requires and caches tiles). AMap (GCJ-02) stays as a WHOLE-MAP
// fallback. Data is stored raw WGS-84 (lib/geo.ts): 天地图 plots it as-is, AMap
// needs wgs84ToGcj02. The two datums must NEVER be mixed on one map (~400m
// drift), so fallback re-creates the whole map + re-projects every coordinate.

export type BaseProvider = "tdt" | "amap";

// 天地图图层经 /tiles 代理。vec=矢量底图 cva=矢量注记 img=影像 cia=影像注记。
const tdt = (layer: "vec" | "cva" | "img" | "cia") =>
  L.tileLayer(`/tiles/${layer}/{z}/{x}/{y}`, { maxZoom: 18, attribution: "© 天地图" });

// 高德栅格瓦片（GCJ-02），天地图整体不可用时整图回退。
export const amapRoad = () =>
  L.tileLayer(
    "https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}",
    { subdomains: "1234", maxZoom: 18, attribution: "© 高德地图" },
  );

export const amapSatellite = () =>
  L.tileLayer("https://webst0{s}.is.autonavi.com/appmaptile?style=6&x={x}&y={y}&z={z}", {
    subdomains: "1234",
    maxZoom: 18,
    attribution: "© 高德地图",
  });

// newAmapMap wires a container into a road+satellite Leaflet map with the
// standard controls. Caller owns the returned map (call .remove() on unmount).
export function newAmapMap(container: HTMLElement): L.Map {
  const road = amapRoad();
  const map = L.map(container, { layers: [road] });
  L.control.layers({ 路网: road, 卫星: amapSatellite() }, undefined, { position: "topright" }).addTo(map);
  L.control.scale({ metric: true, imperial: false }).addTo(map);
  return map;
}

// 投影：数据统一 WGS-84。天地图原样，高德需先转 GCJ-02（火星坐标）。
// 用法：const project = projectFor(provider); project(lat, lng) → [lat, lng]。
export function projectFor(provider: BaseProvider): (lat: number, lng: number) => [number, number] {
  return (lat, lng) => (provider === "amap" ? wgs84ToGcj02(lat, lng) : [lat, lng]);
}

// 天地图为主、高德兜底的底图工厂。provider==='amap' 直接建高德图；'tdt' 建天地图，
// 并在底图(vec)瓦片连续失败到阈值时回调 onFallback——页面据此 setProvider('amap')
// 触发整图重建 + 坐标按高德 GCJ-02 重投影。
const TDT_FAIL_LIMIT = 6;
export function newBaseMap(
  container: HTMLElement,
  provider: BaseProvider,
  onFallback: () => void,
): L.Map {
  if (provider === "amap") return newAmapMap(container);
  const vec = tdt("vec");
  const road = L.layerGroup([vec, tdt("cva")]);
  const sat = L.layerGroup([tdt("img"), tdt("cia")]);
  const map = L.map(container, { layers: [road] });
  L.control.layers({ 路网: road, 卫星: sat }, undefined, { position: "topright" }).addTo(map);
  L.control.scale({ metric: true, imperial: false }).addTo(map);
  let fails = 0;
  vec.on("tileerror", () => {
    if (++fails >= TDT_FAIL_LIMIT) onFallback();
  });
  return map;
}
